import { ErrorBanner } from "@/components/board/ErrorBanner";
import { LoadingState } from "@/components/board/LoadingState";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import { useRecentlyChangedRowsApi } from "@/contexts/RecentlyChangedRowsContext";
import { useRoles } from "@/hooks/useRoles";
import { roleColorStyleFor, type Role } from "@/lib/role-formatting";
import { db, supabase } from "@/lib/supabase";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

const DEFAULT_NEW_ROLE_COLOR = "#475569";

/** Keep the dragged row on the vertical axis. */
const restrictToVerticalAxis: Modifier = ({ transform }) => ({ ...transform, x: 0 });

/**
 * Clamp the dragged row within its parent (the tbody) so it can't be dragged
 * past the top or bottom of the list.
 */
const restrictToParentElement: Modifier = ({ containerNodeRect, draggingNodeRect, transform }) => {
  if (!draggingNodeRect || !containerNodeRect) return transform;
  const value = { ...transform };
  if (draggingNodeRect.top + transform.y <= containerNodeRect.top) {
    value.y = containerNodeRect.top - draggingNodeRect.top;
  } else if (
    draggingNodeRect.bottom + transform.y >=
    containerNodeRect.top + containerNodeRect.height
  ) {
    value.y = containerNodeRect.top + containerNodeRect.height - draggingNodeRect.bottom;
  }
  return value;
};

/** Counts how many employees / assignments reference a role. */
async function countRoleUsage(roleId: string): Promise<{ employees: number; assignments: number }> {
  const [emp, asg] = await Promise.all([
    supabase.from("employees").select("id", { count: "exact", head: true }).contains("role", [roleId]),
    supabase
      .from("project_assignments")
      .select("id", { count: "exact", head: true })
      .eq("required_role_id", roleId),
  ]);
  // A Supabase query error resolves (it doesn't reject) with count: null, so we
  // must surface it explicitly -- otherwise a failed count silently reads as 0,
  // making an in-use role look deletable.
  if (emp.error) throw emp.error;
  if (asg.error) throw asg.error;
  return { employees: emp.count ?? 0, assignments: asg.count ?? 0 };
}

export function RolesSettings() {
  const { roles, loading, error, refresh } = useRoles();
  const { addToast } = useToast();
  const { markSelfEdit } = useRecentlyChangedRowsApi();

  const [editing, setEditing] = useState<Role | null>(null);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<Role | null>(null);

  // Local ordered copy so drag reorders feel instant; reconciled from context.
  const [orderedRoles, setOrderedRoles] = useState<Role[]>(roles);
  useEffect(() => {
    setOrderedRoles(roles);
  }, [roles]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const nextSortOrder =
    orderedRoles.length > 0 ? Math.max(...orderedRoles.map((r) => r.sort_order)) + 1 : 0;

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIndex = orderedRoles.findIndex((r) => r.id === active.id);
      const newIndex = orderedRoles.findIndex((r) => r.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(orderedRoles, oldIndex, newIndex);
      setOrderedRoles(reordered); // optimistic

      // Persist sort_order = index for every row whose position changed.
      const changed = reordered
        .map((r, index) => ({ r, index }))
        .filter(({ r, index }) => r.sort_order !== index);
      const results = await Promise.all(
        changed.map(({ r, index }) => {
          markSelfEdit("roles", r.id);
          return db.roles.update(r.id, { sort_order: index });
        })
      );
      if (results.some((res) => res.error)) {
        addToast("Failed to save the new order.", "error");
        await refresh();
        return;
      }
      await refresh();
    },
    [orderedRoles, markSelfEdit, addToast, refresh]
  );

  return (
    <section className="settings-section">
      <div className="settings-section__header">
        <h2 className="settings-section__title">Roles</h2>
        <div className="settings-section__actions">
          <button type="button" className="primary-button" onClick={() => setAdding(true)}>
            Add Role
          </button>
        </div>
      </div>

      <p className="roles-settings__hint">Drag the handle on a row to change the order roles appear in.</p>

      {error && <ErrorBanner message={error} entityName="roles" />}

      {loading ? (
        <LoadingState message="Loading roles…" />
      ) : (
        <div className="settings-table-wrapper">
          <table className="settings-table">
            <thead>
              <tr>
                <th className="settings-table__th settings-table__th--drag" aria-label="Reorder" />
                <th className="settings-table__th">Role</th>
                <th className="settings-table__th settings-table__th--access">Actions</th>
              </tr>
            </thead>
            <tbody>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={orderedRoles.map((r) => r.id)} strategy={verticalListSortingStrategy}>
                  {orderedRoles.map((role) => (
                    <SortableRoleRow
                      key={role.id}
                      role={role}
                      onEdit={() => setEditing(role)}
                      onDelete={() => setDeleting(role)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </tbody>
          </table>
        </div>
      )}

      {(adding || editing) && (
        <RoleEditModal
          role={editing}
          nextSortOrder={nextSortOrder}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSaved={async () => {
            setAdding(false);
            setEditing(null);
            await refresh();
            addToast(editing ? "Role updated." : "Role added.", "success");
          }}
        />
      )}

      {deleting && (
        <RoleDeleteModal
          role={deleting}
          otherRoles={roles.filter((r) => r.id !== deleting.id)}
          onClose={() => setDeleting(null)}
          onDeleted={async () => {
            setDeleting(null);
            await refresh();
            addToast("Role deleted.", "success");
          }}
        />
      )}
    </section>
  );
}

interface SortableRoleRowProps {
  role: Role;
  onEdit: () => void;
  onDelete: () => void;
}

/** A draggable role row. Bamboo-managed roles can be reordered but not edited/deleted. */
function SortableRoleRow({ role, onEdit, onDelete }: SortableRoleRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: role.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : undefined,
  };
  const managed = role.is_bamboo_managed;

  return (
    <tr ref={setNodeRef} style={style} className="settings-table__row" {...attributes}>
      <td className="settings-table__cell settings-table__cell--drag">
        <button
          type="button"
          className="roles-settings__drag-handle"
          {...listeners}
          tabIndex={-1}
          aria-label={`Drag ${role.display_name} to reorder`}
        >
          <GripVertical size={14} />
        </button>
      </td>
      <td className="settings-table__cell">
        <span className="role-badge" style={roleColorStyleFor(role)}>
          {role.display_name}
        </span>
      </td>
      <td className="settings-table__cell settings-table__cell--access">
        <div className="roles-settings__row-actions">
          <button
            type="button"
            className="icon-button"
            onClick={onEdit}
            aria-label={`Edit ${role.display_name}`}
          >
            <Pencil size={16} />
          </button>
          <button
            type="button"
            className="icon-button"
            onClick={onDelete}
            disabled={managed}
            title={managed ? "Bamboo role — can't be deleted" : undefined}
            aria-label={`Delete ${role.display_name}`}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </td>
    </tr>
  );
}

interface RoleEditModalProps {
  role: Role | null; // null = add
  /** sort_order assigned to a newly-created role (appended to the end). */
  nextSortOrder: number;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

function RoleEditModal({ role, nextSortOrder, onClose, onSaved }: RoleEditModalProps) {
  const { markSelfEdit } = useRecentlyChangedRowsApi();
  // Bamboo-managed roles can change color but not name.
  const managed = !!role?.is_bamboo_managed;
  const [name, setName] = useState(role?.display_name ?? "");
  const [color, setColor] = useState(role?.color ?? DEFAULT_NEW_ROLE_COLOR);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValid = name.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || saving) return;
    setSaving(true);
    setError(null);
    try {
      if (role) {
        // Sort order is changed by dragging in the list, not here. Bamboo roles
        // can change color but keep their name.
        markSelfEdit("roles", role.id);
        const updates = managed ? { color } : { display_name: name.trim(), color };
        const { error: updateError } = await db.roles.update(role.id, updates);
        if (updateError) {
          setError(friendlyRoleError(updateError.message, name.trim()));
          return;
        }
      } else {
        const { data, error: insertError } = await db.roles.create({
          display_name: name.trim(),
          color,
          sort_order: nextSortOrder,
        });
        if (insertError) {
          setError(friendlyRoleError(insertError.message, name.trim()));
          return;
        }
        if (data?.id) markSelfEdit("roles", data.id);
      }
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save role");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={role ? "Edit Role" : "Add Role"} onClose={onClose} size="small">
      <form className="role-form" onSubmit={handleSubmit}>
        {error && (
          <div className="error-banner">
            <span className="error-banner__message">{error}</span>
          </div>
        )}

        <div className="form-field">
          <label className="form-label" htmlFor="role-name">
            Name <span className="required">*</span>
          </label>
          <input
            id="role-name"
            type="text"
            className="form-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Researcher"
            disabled={managed}
            title={managed ? "Bamboo role name can't be changed" : undefined}
            autoFocus={!managed}
          />
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="role-color">
            Color
          </label>
          <div className="role-form__color-row">
            <input
              id="role-color"
              type="color"
              className="role-form__color-input"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              aria-label="Role color"
              autoFocus={managed}
            />
            <span className="role-badge" style={roleColorStyleFor({ ...(role ?? ({} as Role)), color })}>
              {name.trim() || "Preview"}
            </span>
          </div>
        </div>

        <div className="modal-actions">
          <button type="button" className="secondary-button" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="primary-button" disabled={!isValid || saving}>
            {saving ? "Saving…" : role ? "Save" : "Add Role"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

interface RoleDeleteModalProps {
  role: Role;
  otherRoles: Role[];
  onClose: () => void;
  onDeleted: () => void | Promise<void>;
}

function RoleDeleteModal({ role, otherRoles, onClose, onDeleted }: RoleDeleteModalProps) {
  const { markSelfEdit } = useRecentlyChangedRowsApi();
  const [usage, setUsage] = useState<{ employees: number; assignments: number } | null>(null);
  const [usageError, setUsageError] = useState(false);
  const [reassignTo, setReassignTo] = useState(otherRoles[0]?.id ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load usage counts on open.
  useEffect(() => {
    let cancelled = false;
    countRoleUsage(role.id)
      .then((u) => {
        if (cancelled) return;
        setUsage(u);
        setUsageError(false);
      })
      // If we can't determine usage, block deletion rather than assuming the role
      // is unused -- deleting an in-use role would orphan role IDs on employees
      // and hit ON DELETE RESTRICT on project_assignments.
      .catch(() => !cancelled && setUsageError(true));
    return () => {
      cancelled = true;
    };
  }, [role.id]);

  const inUse = !!usage && (usage.employees > 0 || usage.assignments > 0);

  const handleDelete = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (inUse) {
        if (!reassignTo) {
          setError("Choose a role to reassign to.");
          return;
        }
        const reassigned = await reassignRole(role.id, reassignTo, markSelfEdit);
        if (!reassigned.ok) {
          setError(reassigned.message);
          return;
        }
      }
      markSelfEdit("roles", role.id);
      const { error: deleteError } = await db.roles.delete(role.id);
      if (deleteError) {
        setError(deleteError.message);
        return;
      }
      await onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete role");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Delete "${role.display_name}"`} onClose={onClose} size="small">
      <div className="role-form">
        {error && (
          <div className="error-banner">
            <span className="error-banner__message">{error}</span>
          </div>
        )}

        {usageError ? (
          <p>Couldn't check where this role is used. Try again before deleting.</p>
        ) : usage === null ? (
          <p>Checking where this role is used…</p>
        ) : inUse ? (
          <>
            <p>
              "{role.display_name}" is used by {usage.employees} employee
              {usage.employees === 1 ? "" : "s"} and {usage.assignments} assignment
              {usage.assignments === 1 ? "" : "s"}. Reassign them to another role before deleting.
            </p>
            <div className="form-field">
              <label className="form-label" htmlFor="reassign-to">
                Reassign to <span className="required">*</span>
              </label>
              <select
                id="reassign-to"
                className="form-input"
                value={reassignTo}
                onChange={(e) => setReassignTo(e.target.value)}
              >
                {otherRoles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.display_name}
                  </option>
                ))}
              </select>
            </div>
          </>
        ) : (
          <p>Are you sure you want to delete "{role.display_name}"?</p>
        )}

        <div className="modal-actions">
          <button type="button" className="secondary-button" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="danger-button"
            onClick={handleDelete}
            disabled={busy || usageError || usage === null || (inUse && !reassignTo)}
          >
            {busy ? "Deleting…" : inUse ? "Reassign & Delete" : "Delete Role"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Reassigns every employee and assignment using `fromId` to `toId`.
 *
 * The employee transform (replace `fromId` with `toId` in a uuid[], de-duped) is
 * per-row derived from the existing column, so it can't be a single PostgREST
 * update. Rather than loop one update per employee -- which has no transactional
 * guarantee and can leave rows partially reassigned if it fails mid-loop -- we
 * delegate to the `reassign_role` SQL function, which moves both tables inside one
 * transaction (all rows commit or none do).
 */
async function reassignRole(
  fromId: string,
  toId: string,
  markSelfEdit: (table: string, id: string) => void
): Promise<{ ok: true } | { ok: false; message: string }> {
  // Pre-mark the affected employees as self-edits so the realtime handler treats
  // the (about-to-arrive) row updates as ours, not external changes. Done before
  // the RPC commits, since realtime events are emitted at commit time.
  const { data: affectedEmployees, error: empFetchError } = await supabase
    .from("employees")
    .select("id")
    .contains("role", [fromId]);
  if (empFetchError) return { ok: false, message: empFetchError.message };
  for (const emp of affectedEmployees ?? []) {
    markSelfEdit("employees", emp.id);
  }

  // Atomic reassignment of employees + assignments in a single transaction.
  const { error } = await supabase.rpc("reassign_role", {
    from_id: fromId,
    to_id: toId,
  });
  if (error) return { ok: false, message: error.message };

  return { ok: true };
}

function friendlyRoleError(message: string, name: string): string {
  if (/duplicate key|unique/i.test(message)) {
    return `A role named "${name}" already exists.`;
  }
  return message;
}
